import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, User, Phone, Mail, Car, Plus, ChevronRight } from 'lucide-react';
import { useCustomerSearch } from '@/hooks/useCustomerSearch';
import { CustomerSearchBar } from '@/components/customers';
import type { CustomerSearchResult } from '@/lib/customers/types';
import { listCustomers } from '@/lib/api/customerApi';

export default function Customers() {
  const navigate = useNavigate();
  // OD-34: technicians and detail specialists read this page; creating a ticket stays with the front office.
  const { canCreateTickets } = usePermissions();
  const { activeShop } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all customers with masked PII via customer-list edge function
  const { data: customers, isLoading: isLoadingCustomers } = useQuery({
    queryKey: ['customers-list', activeShop?.id],
    queryFn: async () => {
      if (!activeShop?.id) return [];
      return listCustomers({
        shop_id: activeShop.id,
        limit: 100,
        order_by: 'full_name',
        ascending: true,
      });
    },
    enabled: !!activeShop?.id && searchQuery.length < 2,
  });

  // Use search hook for filtering
  const { results: searchResults, isSearching, search, clearResults } = useCustomerSearch({
    debounceMs: 300,
    limit: 20,
  });

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      search(query);
    } else {
      clearResults();
    }
  }, [search, clearResults]);

  const handleCustomerClick = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  const handleSearchResultClick = (result: CustomerSearchResult) => {
    if (result.customer_id) {
      navigate(`/customers/${result.customer_id}`);
    }
  };

  const displayData = searchQuery.length >= 2 ? searchResults : customers;
  const isLoading = searchQuery.length >= 2 ? isSearching : isLoadingCustomers;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Customers &amp; Vehicles</h1>
          <p className="text-muted-foreground">Customer profiles, vehicles and visit history</p>
        </div>
        {/* Full width on a phone so the primary action is never a hard-to-hit
            corner target; inline from sm up. */}
        {canCreateTickets && (
          <Button
            onClick={() => navigate('/create-ticket')}
            className="w-full sm:w-auto"
            data-testid="customers-new-ticket"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, email, VIN, or plate..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
              data-testid="customers-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {searchQuery.length >= 2 
              ? `Search Results (${searchResults?.length || 0})`
              : `All Customers (${customers?.length || 0})`
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : searchQuery.length >= 2 ? (
            // Search results mode
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {searchResults?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No customers found matching "{searchQuery}"
                  </p>
                ) : (
                  searchResults?.map((result, idx) => (
                    <button
                      key={`${result.customer_id}-${idx}`}
                      onClick={() => handleSearchResultClick(result)}
                      className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors flex items-center justify-between"
                      disabled={!result.customer_id}
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                          {result.vehicle_id ? (
                            <Car className="h-5 w-5 text-primary" />
                          ) : (
                            <User className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{result.display_label}</div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            {result.phone_masked && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {result.phone_masked}
                              </span>
                            )}
                            {result.email_masked && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {result.email_masked}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="text-xs">
                          {result.match_type}
                        </Badge>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          ) : customers?.length === 0 ? (
            // ── ZERO STATE ───────────────────────────────────────────────
            // Deliberately NOT inside the fixed-height ScrollArea: there is
            // nothing to scroll, and reserving 500px of blank area is what
            // made this page read as unfinished. It sizes to its content.
            <div
              className="flex flex-col items-center justify-center gap-4 px-4 py-10 text-center sm:py-14"
              data-testid="customers-empty-state"
            >
              <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <User className="h-7 w-7 text-muted-foreground" />
                <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-4 border-background bg-muted">
                  <Car className="h-4 w-4 text-muted-foreground" />
                </span>
              </div>

              <div className="space-y-1.5">
                <h2 className="text-lg font-semibold">No customers yet</h2>
                <p className="mx-auto max-w-md text-sm text-muted-foreground">
                  Customer and vehicle history will build here as you create tickets.
                </p>
                <p className="mx-auto max-w-md text-sm text-muted-foreground">
                  Returning customers, their vehicles and past visits become
                  searchable from this page &mdash; by name, phone, email, VIN or plate.
                </p>
              </div>

              {canCreateTickets && (
                <Button
                  onClick={() => navigate('/create-ticket')}
                  className="w-full sm:w-auto"
                  data-testid="customers-create-first-ticket"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Ticket
                </Button>
              )}
            </div>
          ) : (
            // All customers mode
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {(
                  customers?.map((customer: any) => (
                    <button
                      key={customer.id}
                      onClick={() => handleCustomerClick(customer.id)}
                      className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{customer.full_name}</div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            {customer.phone_masked && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {customer.phone_masked}
                              </span>
                            )}
                            {customer.email_masked && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {customer.email_masked}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {typeof customer.vehicles === 'object' && customer.vehicles !== null && 'count' in customer.vehicles && (
                          <Badge variant="secondary" className="text-xs">
                            <Car className="h-3 w-3 mr-1" />
                            {(customer.vehicles as { count: number }).count} vehicles
                          </Badge>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
